import React, { useEffect, useState, useMemo, useRef } from "react";
import { toast } from "react-hot-toast";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { HiOutlinePlus, HiOutlineX, HiOutlineSparkles } from "react-icons/hi";
import API from "../../api";
import { EXAM_TYPE, EXAM_TYPE_LABEL } from "../../utils";
import { capture } from "../../instrument";
import DependencyExplorer from "../../components/dependencies/DependencyExplorer";
import CodeLookup from "../../components/CodeLookup";
import TableExamples from "../../components/table/TableExamples";

const DEFAULT_EXAM_TYPE_PRIORITY = [
  EXAM_TYPE.DEFAULT,
  EXAM_TYPE.SANG,
  EXAM_TYPE.URINE,
  EXAM_TYPE.ECHANTILLON,
  EXAM_TYPE.H24,
  EXAM_TYPE.SEROLOGIE,
  EXAM_TYPE.PCR,
  EXAM_TYPE.SELLE,
  EXAM_TYPE.AUTRE,
];

const PCR_EXAM_TYPE_PRIORITY = [
  EXAM_TYPE.DEFAULT,
  EXAM_TYPE.SANG,
  EXAM_TYPE.URINE,
  EXAM_TYPE.ECHANTILLON,
  EXAM_TYPE.H24,
  EXAM_TYPE.PCR,
  EXAM_TYPE.SEROLOGIE,
  EXAM_TYPE.SELLE,
  EXAM_TYPE.AUTRE,
];

const CODE_TYPES = [
  { type: EXAM_TYPE.SANG, dotClass: "bg-red-500" },
  { type: EXAM_TYPE.SEROLOGIE, dotClass: "bg-yellow-500" },
  { type: EXAM_TYPE.PCR, dotClass: "bg-pink-500" },
  { type: EXAM_TYPE.URINE, dotClass: "bg-primary-500" },
  { type: EXAM_TYPE.SELLE, dotClass: "bg-violet-500" },
  { type: EXAM_TYPE.AUTRE, dotClass: "bg-orange-500" },
];

const EXAMS_TEXTAREA_CLASS =
  "w-full resize-y rounded-md border border-gray-200 bg-white px-3 py-2 text-sm leading-snug text-gray-900 placeholder:text-gray-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500";

const AddCode = () => {
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [examTypePriority, setExamTypePriority] = useState([]);
  const [isTypeMenuOpen, setIsTypeMenuOpen] = useState(false);
  const typeMenuRef = useRef(null);

  const [suggestionData, setSuggestionData] = useState({ codes: "", exams: [] });
  const [codes, setCodes] = useState({
    [EXAM_TYPE.SANG]: [],
  });
  const [exams, setExams] = useState({
    [EXAM_TYPE.SANG]: "",
  });

  const currentCodes = useMemo(() => {
    return Object.values(codes)
      .flat()
      .filter(Boolean)
      .map((c) => c.value);
  }, [codes]);

  const { data: diagramData } = useQuery({
    queryKey: ["diagramData"],
    queryFn: async () => {
      const [questions, guidelines, actions] = await Promise.all([API.get("/question"), API.get("/guideline"), API.get("/action")]);
      if (!questions.ok || !guidelines.ok || !actions.ok) throw new Error("Failed to load diagram data");
      return { questions: questions.data, guidelines: guidelines.data, actions: actions.data };
    },
  });

  useEffect(() => {
    setExams((prev) => {
      const types = Object.keys(codes).filter((type) => codes[type] !== undefined);

      const examsToRemove = Object.keys(prev).filter((type) => !types.includes(type));
      const examsToAdd = types.filter((type) => prev[type] === undefined);

      let newExams = JSON.parse(JSON.stringify(prev));
      examsToRemove.forEach((type) => {
        newExams[type] = undefined;
      });

      examsToAdd.forEach((type) => {
        newExams[type] = "";
      });

      if (prev[EXAM_TYPE.DEFAULT]) newExams[EXAM_TYPE.DEFAULT] = prev[EXAM_TYPE.DEFAULT];
      else if (types.filter((type) => type !== EXAM_TYPE.ECHANTILLON && type !== EXAM_TYPE.H24).length > 1) newExams[EXAM_TYPE.DEFAULT] = "";
      return newExams;
    });

    if (codes[EXAM_TYPE.PCR] === undefined || codes[EXAM_TYPE.SEROLOGIE] === undefined) setExamTypePriority(DEFAULT_EXAM_TYPE_PRIORITY);
  }, [codes]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const codesParam = urlParams.get("codes")?.split(",");
    const codeIdsParam = urlParams.get("codeIds")?.split(",");
    const examsParam = urlParams.get("exams")?.split(",");
    const typeParam = urlParams.get("type");
    const isUrineType = [EXAM_TYPE.URINE, EXAM_TYPE.ECHANTILLON, EXAM_TYPE.H24].includes(typeParam);
    const suggestedType = isUrineType || CODE_TYPES.some(({ type }) => type === typeParam) ? typeParam : EXAM_TYPE.SANG;
    const suggestedCodes = codesParam?.map((value, index) => ({ _id: codeIdsParam?.[index], value: value.trim() }));
    const urineBuckets = isUrineType ? { [EXAM_TYPE.URINE]: [], [EXAM_TYPE.ECHANTILLON]: [], [EXAM_TYPE.H24]: [] } : {};
    if (codesParam) setCodes({ ...urineBuckets, [suggestedType]: suggestedCodes });
    if (examsParam) setExams({ [suggestedType]: examsParam.join(", ") });
    setSuggestionData({ codes: codesParam, exams: examsParam, type: suggestedType });
  }, [location.search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (typeMenuRef.current && !typeMenuRef.current.contains(event.target)) setIsTypeMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const createMutation = useMutation({
    mutationFn: (body) => API.post("/table", body),
    onSuccess: (response) => {
      if (!response.ok) {
        if (response.code === "CODE_ALREADY_EXISTS") return toast.error("Ce code existe déjà, choisissez-le dans les suggestions");
        if (response.code === "CODE_NOT_FOUND") return toast.error("Un des codes sélectionnés est introuvable");
        return toast.error(response.message || "Une erreur est survenue");
      }
      toast.success("Code ajouté avec succès");
      queryClient.invalidateQueries({ queryKey: ["tables"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
      history.push("/table");
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const addCode = () => {
    let body = { codes: [], exams: [], EXAM_TYPE_PRIORITY: examTypePriority };

    for (const [type, codeObjects] of Object.entries(codes)) {
      if (!codeObjects?.length) continue;
      body.codes.push(
        ...codeObjects.map((c) => ({
          _id: c._id,
          type,
          value: c.value,
        })),
      );
    }

    for (const [type, value] of Object.entries(exams)) {
      if (!value) continue;
      body.exams.push(
        ...value
          .split(",")
          .filter((value) => value.length > 0)
          .map((value) => ({ type, value: value.trim() })),
      );
    }

    if (!body.codes.length) return toast.error("Merci de saisir un code");
    createMutation.mutate(body);
  };

  const onAddType = (type) => {
    if (type === EXAM_TYPE.URINE) setCodes((prev) => ({ ...prev, [EXAM_TYPE.URINE]: [], [EXAM_TYPE.ECHANTILLON]: [], [EXAM_TYPE.H24]: [] }));
    else setCodes((prev) => ({ ...prev, [type]: [] }));
  };

  const onRemoveType = (type) => {
    if (type === EXAM_TYPE.URINE) setCodes((prev) => ({ ...prev, [EXAM_TYPE.URINE]: undefined, [EXAM_TYPE.ECHANTILLON]: undefined, [EXAM_TYPE.H24]: undefined }));
    else setCodes((prev) => ({ ...prev, [type]: undefined }));
  };

  const missingTypes = CODE_TYPES.filter(({ type }) => codes[type] === undefined);
  const fallbackType = examTypePriority.find((type) => codes[type]?.length > 0);

  return (
    <div className="flex h-[calc(100vh-60px)] flex-col bg-gray-50">
      <div className="flex items-start justify-between border-b border-gray-200 bg-white px-8 pb-5 pt-6">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-gray-900">Ajouter une table</h1>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 pl-4">
          <button
            type="button"
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
            onClick={() => history.push("/table")}
          >
            Annuler
          </button>
          <button
            type="button"
            className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            onClick={addCode}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Chargement..." : "Ajouter"}
          </button>
        </div>
      </div>

      <div className="grid flex-1 grid-cols-5 overflow-hidden">
        <div className="col-span-3 overflow-y-auto">
          <div className="mx-auto max-w-4xl px-8 py-6">
            {suggestionData.codes?.length > 0 && (
              <div className="mb-6 flex items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
                <HiOutlineSparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary-500" />
                <div className="min-w-0">
                  <p className="text-sm text-primary-700">
                    Table suggérée pour le code <span className="font-medium">{suggestionData.codes.join(", ")}</span> avec l'examen ci-dessous, dans la catégorie{" "}
                    <span className="font-medium">{EXAM_TYPE_LABEL[suggestionData.type]}</span>. Vérifiez puis cliquez sur Ajouter.
                  </p>
                  {suggestionData.exams?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {suggestionData.exams.map((exam, index) => (
                        <span key={index} className="rounded bg-white px-1.5 py-0.5 text-xs font-medium text-primary-700 ring-1 ring-primary-200">
                          {exam}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <h3 className="mb-4 text-lg font-medium text-gray-900">Général</h3>

            <div className="space-y-3">
              <div className="flex items-baseline justify-between px-1">
                <h4 className="text-sm font-medium text-gray-700">Codes par type</h4>
                <p className="text-xs text-gray-400">Examens séparés par une virgule</p>
              </div>

              {CODE_TYPES.filter(({ type }) => codes[type] !== undefined).map(({ type, dotClass }) => (
                <div key={type} className="rounded-lg border border-gray-100 bg-white">
                  <div className="flex flex-wrap items-center gap-2.5 rounded-t-lg border-b border-gray-100 bg-gray-50/40 px-5 py-2.5">
                    <span className={`h-2 w-2 shrink-0 rounded-full ${dotClass}`} />
                    <span className="text-[13px] font-medium text-gray-800">{EXAM_TYPE_LABEL[type]}</span>
                    <span className="text-xs text-gray-400">
                      {type === EXAM_TYPE.URINE
                        ? "Ces examens donnent toujours des codes urinaires, quel que soit le contexte de l'ordonnance"
                        : `Ces examens donnent toujours les codes ${EXAM_TYPE_LABEL[type]}, quel que soit le contexte de l'ordonnance`}
                    </span>
                    <button
                      type="button"
                      onClick={() => onRemoveType(type)}
                      className="ml-auto rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
                      title={`Supprimer le type ${EXAM_TYPE_LABEL[type]}`}
                    >
                      <HiOutlineX className="h-4 w-4" />
                    </button>
                  </div>

                  {type === EXAM_TYPE.URINE ? (
                    <div className="space-y-4 px-5 py-4">
                      <div>
                        <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Examens communs à Échantillon et 24 heures</label>
                        <textarea
                          rows={2}
                          className={EXAMS_TEXTAREA_CLASS}
                          placeholder="Examen"
                          value={exams[EXAM_TYPE.URINE] || ""}
                          onChange={(e) => setExams((prev) => ({ ...prev, [EXAM_TYPE.URINE]: e.target.value }))}
                        />
                        <p className="mt-1.5 text-xs text-gray-400">L'ordonnance choisit 24 heures ou Échantillon, sinon Échantillon</p>
                      </div>
                      <div className="space-y-4 border-t border-gray-100 pt-4">
                        <div className="flex items-center gap-2">
                          <span className="h-1.5 w-1.5 rounded-full bg-purple-500" />
                          <span className="text-[13px] font-medium text-gray-800">Échantillon (miction)</span>
                        </div>
                        <div>
                          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Codes</label>
                          <CodeLookup
                            initialValues={codes[EXAM_TYPE.ECHANTILLON] || []}
                            onChange={(newCodes) => setCodes((prev) => ({ ...prev, [EXAM_TYPE.ECHANTILLON]: newCodes }))}
                            placeholder="Code"
                          />
                        </div>
                        <div>
                          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Examens</label>
                          <textarea
                            rows={2}
                            className={EXAMS_TEXTAREA_CLASS}
                            placeholder="Examen"
                            value={exams[EXAM_TYPE.ECHANTILLON] || ""}
                            onChange={(e) => setExams((prev) => ({ ...prev, [EXAM_TYPE.ECHANTILLON]: e.target.value }))}
                          />
                        </div>
                      </div>
                      <div className="space-y-4 border-t border-gray-100 pt-4">
                        <div className="flex items-center gap-2">
                          <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                          <span className="text-[13px] font-medium text-gray-800">24 heures</span>
                        </div>
                        <div>
                          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Codes</label>
                          <CodeLookup
                            initialValues={codes[EXAM_TYPE.H24] || []}
                            onChange={(newCodes) => setCodes((prev) => ({ ...prev, [EXAM_TYPE.H24]: newCodes }))}
                            placeholder="Code"
                          />
                        </div>
                        <div>
                          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Examens</label>
                          <textarea
                            rows={2}
                            className={EXAMS_TEXTAREA_CLASS}
                            placeholder="Examen"
                            value={exams[EXAM_TYPE.H24] || ""}
                            onChange={(e) => setExams((prev) => ({ ...prev, [EXAM_TYPE.H24]: e.target.value }))}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4 px-5 py-4">
                      <div>
                        <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Codes</label>
                        <CodeLookup initialValues={codes[type] || []} onChange={(newCodes) => setCodes((prev) => ({ ...prev, [type]: newCodes }))} placeholder="Code" />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Examens</label>
                        <textarea
                          rows={2}
                          className={EXAMS_TEXTAREA_CLASS}
                          placeholder="Examen"
                          value={exams[type] || ""}
                          onChange={(e) => setExams((prev) => ({ ...prev, [type]: e.target.value }))}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {exams[EXAM_TYPE.DEFAULT] !== undefined && (
                <div className="rounded-lg border border-gray-100 bg-white">
                  <div className="flex flex-wrap items-center gap-2.5 rounded-t-lg border-b border-gray-100 bg-gray-50/40 px-5 py-2.5">
                    <span className="h-2 w-2 shrink-0 rounded-full bg-gray-400" />
                    <span className="text-[13px] font-medium text-gray-800">Examens communs à tous les types</span>
                    <span className="text-xs text-gray-400">Le contexte de l'ordonnance choisit le type{fallbackType ? `, sinon ${EXAM_TYPE_LABEL[fallbackType]}` : ""}</span>
                  </div>
                  <div className="px-5 py-4">
                    <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Examens</label>
                    <textarea
                      rows={2}
                      className={EXAMS_TEXTAREA_CLASS}
                      placeholder="Examen"
                      value={exams[EXAM_TYPE.DEFAULT] || ""}
                      onChange={(e) => setExams((prev) => ({ ...prev, [EXAM_TYPE.DEFAULT]: e.target.value }))}
                    />
                  </div>
                </div>
              )}

              {missingTypes.length > 0 && (
                <div className="relative px-1 pt-1" ref={typeMenuRef}>
                  <button
                    type="button"
                    onClick={() => setIsTypeMenuOpen(!isTypeMenuOpen)}
                    className="flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700"
                  >
                    <HiOutlinePlus className="h-3.5 w-3.5" />
                    Ajouter un type
                  </button>
                  {isTypeMenuOpen && (
                    <div className="absolute left-1 z-10 mt-1 w-44 rounded-lg border border-gray-200 bg-white shadow-lg">
                      {missingTypes.map(({ type, dotClass }) => (
                        <button
                          key={type}
                          type="button"
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 transition-colors first:rounded-t-lg last:rounded-b-lg hover:bg-gray-50"
                          onClick={() => {
                            setIsTypeMenuOpen(false);
                            onAddType(type);
                          }}
                        >
                          <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
                          {EXAM_TYPE_LABEL[type]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {exams[EXAM_TYPE.PCR] !== undefined && exams[EXAM_TYPE.SEROLOGIE] !== undefined && (
              <div className="mt-6 rounded-lg border border-gray-100 bg-white p-5">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="pcrPriority"
                    className="h-5 w-5 cursor-pointer rounded border-gray-300 text-primary-500 focus:ring-2 focus:ring-primary-500"
                    checked={JSON.stringify(examTypePriority) === JSON.stringify(PCR_EXAM_TYPE_PRIORITY)}
                    onChange={(e) => setExamTypePriority(e.target.checked ? PCR_EXAM_TYPE_PRIORITY : DEFAULT_EXAM_TYPE_PRIORITY)}
                  />
                  <label htmlFor="pcrPriority" className="ml-3 cursor-pointer text-sm font-medium text-gray-700">
                    Codes PCR prioritaires sur les codes Sérologie
                  </label>
                </div>
                <p className="ml-8 mt-2 text-xs text-gray-500">En l'absence de contexte dans l'ordonnance, les codes PCR sont insérés à la place des codes Sérologie.</p>
              </div>
            )}

            <TableExamples codes={codes} exams={exams} examTypePriority={examTypePriority} />
          </div>
        </div>

        <div className="col-span-2 shrink-0 overflow-y-auto border-l border-gray-200 bg-white p-6">
          <h3 className="mb-4 text-lg font-medium text-gray-900">Dépendances</h3>
          {diagramData ? (
            <DependencyExplorer data={diagramData} currentCodes={currentCodes} className="rounded-lg border border-gray-100" />
          ) : (
            <div className="flex items-center justify-center rounded-lg border border-gray-100 py-8 text-sm text-gray-400">Chargement du diagramme...</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddCode;
